import React, { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Check, X, ShieldCheck, Clock } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const emptyStep = () => ({ approver_type: "role", role_id: "", level: 1 });

export default function Approvals() {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.kind === "super_admin";
  const [tab, setTab] = useState("matrix");
  const [actions, setActions] = useState([]);
  const [rules, setRules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [pending, setPending] = useState([]);
  const [saving, setSaving] = useState("");
  const [notes, setNotes] = useState({});

  const load = () => {
    api.get("/approvals/actions").then((r) => setActions(r.data)).catch(() => {});
    api.get("/approvals/rules").then((r) => setRules(r.data)).catch(() => {});
    api.get("/masters/roles").then((r) => setRoles(r.data)).catch(() => {});
  };
  const loadPending = () => api.get("/approvals/pending").then((r) => setPending(r.data)).catch(() => {});
  useEffect(() => { load(); loadPending(); }, []);
  useEffect(() => { if (user && !isAdmin) setTab("pending"); }, [user, isAdmin]);

  const ruleFor = (action) => rules.find((r) => r.action === action);
  const upsertRule = async (action, patch) => {
    const existing = ruleFor(action);
    const body = { action, active: existing?.active ?? true, steps: existing?.steps || [], ...patch };
    setSaving(action);
    try {
      if (existing) await api.put(`/approvals/rules/${existing.id}`, body);
      else await api.post("/approvals/rules", body);
      const { data } = await api.get("/approvals/rules"); setRules(data);
      toast({ title: "Approval rule saved" });
    } catch (e) { toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    setSaving("");
  };

  const deleteRule = async (action) => {
    const existing = ruleFor(action);
    if (!existing) return;
    if (!window.confirm(`Remove the approval rule for "${action}"? This action will no longer require sign-off.`)) return;
    try {
      await api.delete(`/approvals/rules/${existing.id}`);
      const { data } = await api.get("/approvals/rules"); setRules(data);
      toast({ title: "Approval rule removed" });
    } catch (e) { toast({ title: "Delete failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
  };

  const decide = async (id, kind) => {
    try { await api.post(`/approvals/requests/${id}/${kind}`, { note: notes[id] || "" }); toast({ title: kind === "approve" ? "Approved" : "Rejected" }); setNotes((n) => ({ ...n, [id]: "" })); loadPending(); }
    catch (e) { toast({ title: "Action failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="approvals-page">
      <div className="flex items-center gap-2">
        <ShieldCheck className="text-[#004030]" size={22} />
        <h1 className="font-display text-2xl md:text-3xl font-bold text-[#012a1e]">Approvals &amp; Hierarchy</h1>
      </div>
      <p className="text-slate-500 mt-1 text-sm">Define which actions need sign-off, by role or reporting hierarchy, approved in sequence.</p>

      <div className="flex gap-2 mt-5">
        {[...(isAdmin ? [{ k: "matrix", l: "Approval Matrix" }] : []), { k: "pending", l: `Pending Approvals${pending.length ? ` (${pending.length})` : ""}` }].map((t) => (
          <button key={t.k} data-testid={`approvals-tab-${t.k}`} onClick={() => { setTab(t.k); if (t.k === "pending") loadPending(); }}
            className={`text-sm font-medium px-4 py-2 rounded-full transition-colors ${tab === t.k ? "bg-[#004030] text-white" : "bg-[#f0f6f3] text-[#004030]"}`}>{t.l}</button>
        ))}
      </div>

      {tab === "matrix" && isAdmin && (
        <div className="mt-6 space-y-4" data-testid="approval-matrix">
          {actions.map((a) => {
            const rule = ruleFor(a.key);
            const steps = rule?.steps || [];
            return (
              <div key={a.key} className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={`rule-${a.key}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-display font-bold text-[#012a1e]">{a.label}</p>
                    <p className="text-xs text-slate-500">{steps.length ? `${steps.length} approval step${steps.length > 1 ? "s" : ""} (sequential)` : "No approval required"}{rule && rule.active === false ? " · inactive" : ""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {rule && (
                      <button data-testid={`rule-toggle-${a.key}`} onClick={() => upsertRule(a.key, { active: !(rule.active ?? true) })}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full ${rule.active ?? true ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{rule.active ?? true ? "Active" : "Inactive"}</button>
                    )}
                    <Button size="sm" variant="outline" data-testid={`add-step-${a.key}`} onClick={() => upsertRule(a.key, { steps: [...steps, emptyStep()] })}><Plus size={14} className="mr-1" /> Add step</Button>
                    {rule && (
                      <button data-testid={`delete-rule-${a.key}`} onClick={() => deleteRule(a.key)} className="text-slate-400 hover:text-red-500 p-1.5" title="Remove this approval rule"><Trash2 size={16} /></button>
                    )}
                  </div>
                </div>
                {steps.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {steps.map((s, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-2 bg-[#f7faf8] rounded-xl p-2.5" data-testid={`rule-${a.key}-step-${i}`}>
                        <span className="text-xs font-bold text-slate-400 w-6">#{i + 1}</span>
                        <select value={s.approver_type} data-testid={`step-type-${a.key}-${i}`}
                          onChange={(e) => { const arr = steps.map((x, j) => j === i ? { ...x, approver_type: e.target.value } : x); upsertRule(a.key, { steps: arr }); }}
                          className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
                          <option value="role">By Role</option>
                          <option value="hierarchy">By Hierarchy</option>
                        </select>
                        {s.approver_type === "role" ? (
                          <select value={s.role_id || ""} data-testid={`step-role-${a.key}-${i}`}
                            onChange={(e) => { const arr = steps.map((x, j) => j === i ? { ...x, role_id: e.target.value } : x); upsertRule(a.key, { steps: arr }); }}
                            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm flex-1 min-w-[140px]">
                            <option value="">Any role</option>
                            {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                          </select>
                        ) : (
                          <select value={s.level || 1} data-testid={`step-level-${a.key}-${i}`}
                            onChange={(e) => { const arr = steps.map((x, j) => j === i ? { ...x, level: parseInt(e.target.value, 10) } : x); upsertRule(a.key, { steps: arr }); }}
                            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
                            <option value={1}>Manager (L1)</option>
                            <option value={2}>Manager's manager (L2)</option>
                            <option value={3}>Level 3</option>
                          </select>
                        )}
                        <button data-testid={`remove-step-${a.key}-${i}`} onClick={() => upsertRule(a.key, { steps: steps.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-500 ml-auto"><Trash2 size={15} /></button>
                      </div>
                    ))}
                  </div>
                )}
                {saving === a.key && <p className="text-xs text-slate-400 mt-2 flex items-center gap-1"><Loader2 className="animate-spin" size={12} /> Saving…</p>}
              </div>
            );
          })}
        </div>
      )}

      {tab === "pending" && (
        <div className="mt-6 space-y-3" data-testid="pending-approvals">
          {pending.length === 0 && <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center text-slate-400">No pending approvals.</div>}
          {pending.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={`pending-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-[#012a1e] text-sm">{r.summary}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Requested by {r.requested_by_name} · Step {r.current_step + 1}/{r.steps.length} · <span className="text-[#004030] font-medium">{r.current_step_label}</span></p>
                  <div className="flex gap-1.5 mt-2">
                    {r.steps.map((s, i) => (
                      <span key={i} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.status === "Approved" ? "bg-emerald-100 text-emerald-700" : s.status === "Rejected" ? "bg-red-100 text-red-700" : i === r.current_step ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400"}`}>#{i + 1} {s.status}</span>
                    ))}
                  </div>
                </div>
                {r.can_act ? (
                  <div className="flex flex-col gap-2 shrink-0 w-44">
                    <input value={notes[r.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))} placeholder="Note / reason (optional)" data-testid={`decision-note-${r.id}`} className="h-9 rounded-md border border-slate-200 px-2 text-xs w-full" />
                    <div className="flex gap-2">
                      <button data-testid={`approve-${r.id}`} onClick={() => decide(r.id, "approve")} className="flex items-center gap-1 text-xs font-semibold text-white bg-[#004030] hover:bg-[#013d2b] rounded-full px-3 py-1.5"><Check size={14} /> Approve</button>
                      <button data-testid={`reject-${r.id}`} onClick={() => decide(r.id, "reject")} className="flex items-center gap-1 text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 rounded-full px-3 py-1.5"><X size={14} /> Reject</button>
                    </div>
                  </div>
                ) : (
                  <span className="text-xs text-slate-400 flex items-center gap-1 shrink-0"><Clock size={13} /> Awaiting others</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
